import React, { useMemo } from "react";
import { Input, InputGroup, InputGroupText } from "reactstrap";
import { LoginInput, LoginButton } from "../styled";

const getPasswordStrength = (password) => {
  if (!password) return { strength: 0, label: "", color: "#ccc" };

  let strength = 0;
  const checks = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    numbers: /\d/.test(password),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
  };

  Object.values(checks).forEach((check) => {
    if (check) strength++;
  });

  let label = "";
  let color = "#ccc";

  if (strength === 1) {
    label = "Weak";
    color = "#dc3545";
  } else if (strength === 2) {
    label = "Fair";
    color = "#fd7e14";
  } else if (strength === 3) {
    label = "Good";
    color = "#ffc107";
  } else if (strength === 4) {
    label = "Strong";
    color = "#28a745";
  } else if (strength === 5) {
    label = "Very Strong";
    color = "#20c997";
  }

  return { strength, label, color };
};

// Password requirements checklist component
const PasswordRequirements = ({ password }) => {
  const checks = {
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    numbers: /\d/.test(password),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    length: password.length >= 8,
  };

  return (
    <div
      style={{
        backgroundColor: "#f8f9fa",
        border: "1px solid #e0e0e0",
        borderRadius: "6px",
        padding: "12px",
        marginBottom: "12px",
      }}
    >
      <p
        style={{
          fontSize: "12px",
          fontWeight: "600",
          color: "#333",
          margin: "0 0 8px 0",
        }}
      >
        Password must contain:
      </p>
      <ul
        style={{
          fontSize: "12px",
          color: "#666",
          margin: "0",
          paddingLeft: "18px",
          listStyle: "none",
        }}
      >
        <li style={{ opacity: checks.lowercase ? 1 : 0.5, marginBottom: "4px" }}>
          <span style={{ color: checks.lowercase ? "#28a745" : "#999" }}>
            {checks.lowercase ? "✓" : "○"}
          </span>{" "}
          Lowercase letter
        </li>
        <li style={{ opacity: checks.uppercase ? 1 : 0.5, marginBottom: "4px" }}>
          <span style={{ color: checks.uppercase ? "#28a745" : "#999" }}>
            {checks.uppercase ? "✓" : "○"}
          </span>{" "}
          Uppercase letter
        </li>
        <li style={{ opacity: checks.numbers ? 1 : 0.5, marginBottom: "4px" }}>
          <span style={{ color: checks.numbers ? "#28a745" : "#999" }}>
            {checks.numbers ? "✓" : "○"}
          </span>{" "}
          Number (0-9)
        </li>
        <li style={{ opacity: checks.special ? 1 : 0.5, marginBottom: "4px" }}>
          <span style={{ color: checks.special ? "#28a745" : "#999" }}>
            {checks.special ? "✓" : "○"}
          </span>{" "}
          Special character (!@#$%^&*)
        </li>
        <li style={{ opacity: checks.length ? 1 : 0.5 }}>
          <span style={{ color: checks.length ? "#28a745" : "#999" }}>
            {checks.length ? "✓" : "○"}
          </span>{" "}
          Minimum 8 characters
        </li>
      </ul>
    </div>
  );
};

export const Step4PasswordReferral = ({
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  passwordShown1,
  setPasswordShown1,
  passwordShown2,
  setPasswordShown2,
  referralOption,
  setReferralOption,
  isUserNewsLetters,
  setIsUserNewsLetters,
  isUserTerms,
  setIsUserTerms,
  sources,
  stepErrors,
  setStepErrors,
  onPrevious,
  onSubmit,
  isLoading,
}) => {
  const passwordStrength = useMemo(() => getPasswordStrength(password), [password]);

  return (
    <LoginInput>
      {/* Password Requirements - Always visible */}


      <div className="mb-4 input_password">
        <label>Create Password</label>
        <div className="input-group">
          <InputGroup>
            <Input
              type={passwordShown1 ? "text" : "password"}
              bsSize="lg"
              placeholder="Enter a strong password"
              onChange={(e) => {
                setPassword(e.target.value);
                setStepErrors({ ...stepErrors, password: "" });
              }}
              value={password}
            />
            <InputGroupText>
              <i
                onClick={() => setPasswordShown1(!passwordShown1)}
                style={{ cursor: "pointer" }}
                className={
                  passwordShown1 ? "far fa-eye" : "far fa-eye-slash"
                }
              ></i>
            </InputGroupText>
          </InputGroup>
        </div>
        {stepErrors.password && <h3>{stepErrors.password}</h3>}
        {password && (
          <div style={{ marginTop: "8px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "4px",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "4px",
                  backgroundColor: "#e0e0e0",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${(passwordStrength.strength / 5) * 100}%`,
                    height: "100%",
                    backgroundColor: passwordStrength.color,
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: "12px",
                  fontWeight: "600",
                  color: passwordStrength.color,
                  whiteSpace: "nowrap",
                  minWidth: "80px",
                }}
              >
                {passwordStrength.label}
              </span>
            </div>
          </div>
        )}
      </div>
      <PasswordRequirements password={password} />
      <div className="mb-4 input_password">
        <label>Confirm Password</label>
        <div className="input-group">
          <InputGroup>
            <Input
              type={passwordShown2 ? "text" : "password"}
              bsSize="lg"
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setStepErrors({ ...stepErrors, confirmPassword: "" });
              }}
              value={confirmPassword}
            />
            <InputGroupText>
              <i
                onClick={() => setPasswordShown2(!passwordShown2)}
                style={{ cursor: "pointer" }}
                className={
                  passwordShown2 ? "far fa-eye" : "far fa-eye-slash"
                }
              ></i>
            </InputGroupText>
          </InputGroup>
        </div>
        {stepErrors.confirmPassword && (
          <h3>{stepErrors.confirmPassword}</h3>
        )}
        {confirmPassword && password && confirmPassword !== password && !stepErrors.confirmPassword && (
          <h3 style={{ color: "#dc3545", fontSize: "14px", marginTop: "8px", marginBottom: "0" }}>
            Passwords do not match
          </h3>
        )}
      </div>

      <div className="mb-4">
        <label>How did you hear about us? <span style={{ fontSize: "12px", color: "#999" }}>(Optional)</span></label>
        <select
          className="form-select form-select-lg select-user-field"
          aria-label=".form-select-lg"
          onChange={(e) => setReferralOption(e.target.value)}
          value={referralOption}
        >
          <option value="">Select an option</option>
          {sources &&
            sources.map((source) => (
              <option key={source.id} value={source.id}>
                {source.name}
              </option>
            ))}
        </select>
      </div>

      <div>
        {/* <div className="form-check">
          <Input
            className="form-check-input"
            type="checkbox"
            id="checkNewsLetter"
            onChange={(e) => setIsUserNewsLetters(e.target.checked)}
            checked={isUserNewsLetters}
          />
          <label
            className="form-check-label"
            htmlFor="checkNewsLetter"
            style={{ cursor: "pointer", marginLeft: "8px" }}
          >
            <strong> Join Our Newsletter</strong>
            <br />
            <span style={{ fontSize: "12px", color: "#666" }}>
              Get treasury tips, market updates & exclusive offers
            </span>
          </label>
        </div> */}

        {/* <div className="form-check" style={{ marginTop: "12px" }}>
          <input
            className="form-check-input"
            type="checkbox"
            id="checkTerms"
            onChange={(e) => {
              setIsUserTerms(e.target.checked);
              setStepErrors({ ...stepErrors, terms: "" });
            }}
            checked={isUserTerms}
          />
          <label
            className="form-check-label"
            htmlFor="checkTerms"
            style={{ cursor: "pointer", marginLeft: "8px" }}
          >
            I agree to the{" "}
            <span
              style={{
                color: "rgba(17, 30, 108, 1)",
                fontWeight: "500",
              }}
            >
              Terms
            </span>{" "}
            and{" "}
            <span
              style={{
                color: "rgba(17, 30, 108, 1)",
                fontWeight: "500",
              }}
            >
              Privacy
            </span>{" "}
            Policy
          </label>
        </div>
        {stepErrors.terms && <h3>{stepErrors.terms}</h3>} */}
      </div>

      <LoginButton>
        <div className="text-center mt-5">
          <button
            type="button"
            className="btn btn-secondary px-5 mb-2"
            onClick={onPrevious}
            disabled={isLoading}
          >
            Previous
          </button>
          <button
            type="submit"
            className="btn btn-primary px-5 mb-2"
            disabled={
              isLoading || (password && confirmPassword && password !== confirmPassword)
            }
          >
            {isLoading ? "Creating Account..." : "Create Account"}
          </button>
        </div>
      </LoginButton>
    </LoginInput>
  );
};
